/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * Generates the theme object for the component from the theme and provided additional information
 * @param  {Object} theme The actual theme object.
 * @return {Object} The final theme object with the overrides and component variables
 */
const generateComponentTheme = theme => {
  const colors = theme.colors,
    borders = theme.borders,
    spacing = theme.spacing,
    typography = theme.typography,
    themeName = theme.key;
  const themeSpecificStyle = {
    canvas: {
      focusBorderColor: theme['ic-brand-primary'],
      labelColor: theme['ic-brand-font-color-dark'],
      checkedLabelColor: theme['ic-brand-font-color-dark'],
      checkedBackground: theme['ic-brand-font-color-dark'],
      checkedBorderColor: theme['ic-brand-font-color-dark'],
      hoverBorderColor: theme['ic-brand-font-color-dark']
    }
  };
  const componentVariables = {
    color: colors === null || colors === void 0 ? void 0 : colors.textLightest,
    borderWidth: borders === null || borders === void 0 ? void 0 : borders.widthSmall,
    borderColor: colors === null || colors === void 0 ? void 0 : colors.borderMedium,
    borderRadius: borders === null || borders === void 0 ? void 0 : borders.radiusMedium,
    background: colors === null || colors === void 0 ? void 0 : colors.backgroundLightest,
    marginRight: spacing === null || spacing === void 0 ? void 0 : spacing.xSmall,
    padding: spacing === null || spacing === void 0 ? void 0 : spacing.xxxSmall,
    checkedBackground: colors === null || colors === void 0 ? void 0 : colors.backgroundDarkest,
    checkedBorderColor: colors === null || colors === void 0 ? void 0 : colors.borderDarkest,
    hoverBorderColor: colors === null || colors === void 0 ? void 0 : colors.borderDarkest,
    focusBorderColor: colors === null || colors === void 0 ? void 0 : colors.borderBrand,
    focusBorderWidth: borders === null || borders === void 0 ? void 0 : borders.widthMedium,
    focusBorderStyle: borders === null || borders === void 0 ? void 0 : borders.style,
    labelColor: colors === null || colors === void 0 ? void 0 : colors.textDarkest,
    checkedLabelColor: colors === null || colors === void 0 ? void 0 : colors.textDarkest,
    labelFontFamily: typography === null || typography === void 0 ? void 0 : typography.fontFamily,
    labelFontWeight: typography === null || typography === void 0 ? void 0 : typography.fontWeightNormal,
    labelLineHeight: typography === null || typography === void 0 ? void 0 : typography.lineHeightCondensed,
    facadeSizeSmall: '1rem',
    facadeSizeMedium: '1.25rem',
    facadeSizeLarge: '1.75rem',
    labelFontSizeSmall: typography === null || typography === void 0 ? void 0 : typography.fontSizeSmall,
    labelFontSizeMedium: typography === null || typography === void 0 ? void 0 : typography.fontSizeMedium,
    labelFontSizeLarge: typography === null || typography === void 0 ? void 0 : typography.fontSizeLarge,
    iconSizeSmall: '0.625rem',
    iconSizeMedium: '0.75rem',
    iconSizeLarge: '1rem'
  };
  return {
    ...componentVariables,
    ...themeSpecificStyle[themeName]
  };
};
export default generateComponentTheme;